import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileUp, Upload, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DocumentService } from "@/integrations/api/services/documentService";
import {
  DepositableTransaction,
  DOC_CATEGORY_DEPOSIT_BATCH,
  ImageMimeType,
} from "@/types/documents";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES: ImageMimeType[] = ["image/jpeg", "image/png", "image/webp"];

interface MultipleCashDepositModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function formatMoney(amount: number) {
  return `₹${(Number(amount) || 0).toLocaleString("en-IN")}`;
}

export default function MultipleCashDepositModal({
  open,
  onOpenChange,
  onSuccess,
}: MultipleCashDepositModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transactions, setTransactions] = useState<DepositableTransaction[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDepositable = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DocumentService.listDepositable(100);
      setTransactions(data.transactions || []);
      setSelectedIds([]);
      setFile(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load cash-in-hand transactions"
      );
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadDepositable();
    }
  }, [open, loadDepositable]);

  const selectedRows = useMemo(
    () => transactions.filter((t) => selectedIds.includes(t.transaction_id)),
    [transactions, selectedIds]
  );

  const totalAmount = useMemo(
    () => selectedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [selectedRows]
  );

  const toggleRow = (txnId: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, txnId])] : prev.filter((id) => id !== txnId)
    );
  };

  const handleFile = (next: File | null) => {
    if (!next) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_TYPES.includes(next.type as ImageMimeType)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    if (next.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5 MB");
      return;
    }
    setFile(next);
  };

  const canSubmit = selectedIds.length > 0 && !!file && !submitting;

  const handleSubmit = async () => {
    if (!file || selectedRows.length === 0) return;

    const first = selectedRows[0];
    if (!first?.loan_id) {
      toast.error("Selected transactions are missing loan information");
      return;
    }

    setSubmitting(true);
    try {
      const contentType = file.type as ImageMimeType;
      const ext =
        contentType === "image/png" ? "png" : contentType === "image/webp" ? "webp" : "jpg";
      const fileName = file.name || `deposit-slip-${Date.now()}.${ext}`;

      const presignRes = await DocumentService.presign({
        loan_application_id: first.loan_id,
        filename: fileName,
        content_type: contentType,
        doc_category_id: DOC_CATEGORY_DEPOSIT_BATCH,
      });

      await DocumentService.uploadToS3(presignRes as any, file, contentType);

      const result = await DocumentService.depositBatch({
        transaction_ids: selectedRows.map((r) => r.transaction_id),
        s3_key: presignRes.s3_key,
        file_name: fileName,
        doc_category_id: DOC_CATEGORY_DEPOSIT_BATCH,
        notes: null,
      });

      toast.success(
        result.message ||
          `Deposit slip submitted for ${selectedRows.length} payment(s) · ${formatMoney(result.total_amount ?? totalAmount)}`
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to submit deposit batch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto p-6 sm:rounded-xl">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <FileUp className="h-4 w-4 shrink-0" />
            Upload Receipt for Multiple Cash Deposit
          </DialogTitle>
          <p className="text-sm font-normal text-gray-500">
            Record cash collected against several loans in one go.
          </p>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Cash in Hand – Yes</label>
            <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
              {loading ? (
                <div className="px-3 py-8 text-center text-sm text-gray-500">Loading…</div>
              ) : transactions.length === 0 ? (
                <div className="px-3 py-4 text-xs text-gray-400">
                  No cash-in-hand entries pending a receipt.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {transactions.map((row) => {
                    const checked = selectedIds.includes(row.transaction_id);
                    return (
                      <li key={row.transaction_id}>
                        <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) =>
                              toggleRow(row.transaction_id, v === true)
                            }
                            className="border-gray-300 data-[state=checked]:border-slate-900 data-[state=checked]:bg-slate-900"
                          />
                          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                            {row.applicant_name || "—"}
                          </span>
                          <span className="shrink-0 text-xs text-gray-500">
                            {row.loan_id}
                          </span>
                          <span className="shrink-0 text-sm font-semibold text-green-600">
                            {formatMoney(row.amount)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
              <span className="text-sm font-semibold text-gray-700">Total Amount</span>
              <span className="text-lg font-bold text-gray-900">
                {formatMoney(totalAmount)}
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Upload Proof</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              className="mt-2 h-[42px] w-full gap-2 border-gray-300 font-medium text-gray-800"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              <Upload className="h-4 w-4 text-gray-600" />
              Upload Deposit Slip
            </Button>
            {file && (
              <div className="mt-2.5 flex items-center gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" />
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                  {file.name}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700"
                  onClick={() => {
                    handleFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-9 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
